// TODO: Factor each detail panel (Derived, Window, Comparison, Material) into its own file
import React from 'react'
import PropTypes from 'prop-types'
import SydSelect from '../../../commonDesign/SydSelect'
import PersonalSpace from '../../../molecules/PersonalSpace'
import { CALC_SOURCES, COLUMN_CATALOG_MAP, DATE_MODE_OPTIONS } from '../lib/constants'
import { DERIVE_REGISTRY } from '../lib/derivedColumns'
import { COMPARISON_OPERATION_OPTIONS } from '../lib/comparisonColumns'
import { ALLOCATION_STRATEGY_OPTIONS, WINDOW_REGISTRY } from '../lib/windowColumns'
import { resolveColumnHeader, resolveDefaultHeader } from '../lib/resolveColumnHeader'
import useColumnBuilderStyles from './columnBuilderStyles'
import {
  SORT_DIRECTION_OPTIONS,
  DERIVED_DEP_SOURCE_SECTION_TITLE,
  uniqueCalcSourcesFromInputs,
  derivedHasOverrideableSources,
  parseDateModeValue,
  toDateModeSelectValue
} from './columnBuilderUtils'

function DetailHeader ({ title, catalog, classes }) {
  return (
    <>
      <div className={classes.detailTitle}>{title}</div>
      {catalog?.category && <div className={classes.detailTypeHint}>{catalog.category} — {catalog.label}</div>}
    </>
  )
}

DetailHeader.propTypes = {
  title: PropTypes.string.isRequired,
  catalog: PropTypes.object,
  classes: PropTypes.object.isRequired
}

function ColumnNameInput ({ column, catalog, allColumns, classes, onLabelChange }) {
  const defaultHeader = resolveDefaultHeader(column, catalog, allColumns)
  return (
    <div className={classes.detailSection}>
      <div className={classes.detailSectionLabel}>Column Name</div>
      <input
        type='text'
        className={classes.nameInput}
        placeholder={defaultHeader}
        value={column.label || ''}
        onChange={(e) => onLabelChange(column.id, e.target.value)}
      />
    </div>
  )
}

ColumnNameInput.propTypes = {
  column: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
  allColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
  classes: PropTypes.object.isRequired,
  onLabelChange: PropTypes.func.isRequired
}

function DerivedDetailPanel ({ column, catalog, allColumns, classes, onOptionChange, onLabelChange }) {
  const def = DERIVE_REGISTRY[catalog.deriveId]
  const uniqueSources = uniqueCalcSourcesFromInputs(def?.calcInputs)
  const showCalculationOverrides = derivedHasOverrideableSources(uniqueSources)
  const title = resolveColumnHeader(column, catalog, allColumns)

  const patchDepSource = (sourceName, patch) => {
    onOptionChange(column.id, {
      dependencyOptions: {
        ...column.options?.dependencyOptions,
        [sourceName]: {
          ...column.options?.dependencyOptions?.[sourceName],
          ...patch
        }
      }
    })
  }

  return (
    <div className={classes.detailPanel}>
      <DetailHeader title={title} catalog={catalog} classes={classes} />
      <ColumnNameInput column={column} catalog={catalog} allColumns={allColumns} classes={classes} onLabelChange={onLabelChange} />
      {def?.description ? (
        <div className={classes.emptyHint} style={{ fontStyle: 'normal', marginBottom: 12, textAlign: 'left' }}>
          {def.description}
        </div>
      ) : null}
      {showCalculationOverrides ? (
        <>
          <div className={classes.detailSectionLabel} style={{ marginBottom: 12 }}>Calculation Overrides</div>
          {uniqueSources.map(sourceName => {
            const src = CALC_SOURCES[sourceName]
            if (!src) return null
            const supportsDate = src.supportsDate
            const settingsSchema = src.settingsSchema || []
            if (!supportsDate && settingsSchema.length === 0) return null
            const depOpts = column.options?.dependencyOptions?.[sourceName] || {}
            const sectionTitle = DERIVED_DEP_SOURCE_SECTION_TITLE[sourceName] || sourceName
            const handleDepDateModeChange = (e) => {
              const { dateMode, quickDate } = parseDateModeValue(e.target.value)
              patchDepSource(sourceName, {
                dateMode,
                quickDate,
                startDate: null,
                endDate: null
              })
            }
            const isCustomRange = depOpts.dateMode === 'dateRange'
            return (
              <div key={sourceName} className={classes.detailSection}>
                <div className={classes.detailSectionLabel}>{sectionTitle}</div>
                {supportsDate ? (
                  <>
                    <div className={classes.detailField}>
                      <div className={classes.detailFieldLabel}>Date range</div>
                      <SydSelect
                        options={DATE_MODE_OPTIONS}
                        value={toDateModeSelectValue(depOpts)}
                        onChange={handleDepDateModeChange}
                        size='sm'
                        minWidth='200px'
                      />
                    </div>
                    {isCustomRange ? (
                      <div className={classes.dateRangeRow}>
                        <input
                          type='date'
                          className={classes.dateInput}
                          value={depOpts.startDate || ''}
                          onChange={(e) => patchDepSource(sourceName, { startDate: e.target.value || null })}
                        />
                        <input
                          type='date'
                          className={classes.dateInput}
                          value={depOpts.endDate || ''}
                          onChange={(e) => patchDepSource(sourceName, { endDate: e.target.value || null })}
                        />
                      </div>
                    ) : null}
                  </>
                ) : null}
                {settingsSchema.length > 0 ? (
                  <>
                    {settingsSchema.map(setting => {
                      if (setting.control === 'select') {
                        return (
                          <div key={setting.key} className={classes.detailField}>
                            <div className={classes.detailFieldLabel}>{setting.label}</div>
                            <SydSelect
                              options={setting.options}
                              value={depOpts[setting.key] ?? setting.defaultValue}
                              onChange={(e) => patchDepSource(sourceName, { [setting.key]: e.target.value })}
                              size='sm'
                              minWidth='200px'
                            />
                          </div>
                        )
                      }
                      if (setting.control === 'checkbox') {
                        return (
                          <label key={setting.key} className={classes.settingCheckLabel}>
                            <input
                              type='checkbox'
                              checked={depOpts[setting.key] ?? setting.defaultValue}
                              onChange={(e) => patchDepSource(sourceName, { [setting.key]: e.target.checked })}
                            />
                            {setting.label}
                          </label>
                        )
                      }
                      return null
                    })}
                  </>
                ) : null}
              </div>
            )
          })}
        </>
      ) : null}
      <PersonalSpace size={60} />
    </div>
  )
}

DerivedDetailPanel.propTypes = {
  column: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
  allColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
  classes: PropTypes.object.isRequired,
  onOptionChange: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired
}

function WindowDetailPanel ({ column, catalog, allColumns, classes, onOptionChange, onLabelChange }) {
  const regEntry = WINDOW_REGISTRY[catalog.windowType]
  const isRowScoped = regEntry?.scope === 'row'
  const title = resolveColumnHeader(column, catalog, allColumns)

  const materialColumns = allColumns.filter(c => {
    const cat = COLUMN_CATALOG_MAP[c.columnType]
    return cat && cat.kind !== 'window' && cat.kind !== 'derived' && cat.measureKind === 'currency'
  })
  const sourceMeasureOptions = materialColumns.map(c => {
    const cat = COLUMN_CATALOG_MAP[c.columnType]
    return { value: c.id, label: c.label ?? cat?.label ?? c.columnType }
  })

  const selectedSourceId = column.options?.sourceColumnId

  const handleSourceChange = (e) => {
    const instanceId = e.target.value
    const instance = allColumns.find(c => c.id === instanceId)
    onOptionChange(column.id, {
      sourceColumnId: instanceId,
      sourceColumnType: instance?.columnType || column.options?.sourceColumnType
    })
  }

  return (
    <div className={classes.detailPanel}>
      <DetailHeader title={title} catalog={catalog} classes={classes} />
      <ColumnNameInput column={column} catalog={catalog} allColumns={allColumns} classes={classes} onLabelChange={onLabelChange} />
      <div className={classes.detailSection}>
        <div className={classes.detailSectionLabel}>Source Measure</div>
        <div className={classes.detailField}>
          {sourceMeasureOptions.length > 0 ? (
            <SydSelect
              options={sourceMeasureOptions}
              value={selectedSourceId || ''}
              onChange={handleSourceChange}
              size='sm'
              minWidth='200px'
            />
          ) : (
            <div className={classes.emptyHint} style={{ textAlign: 'left' }}>
              Add a currency column (e.g. Market Value) first
            </div>
          )}
        </div>
      </div>
      {isRowScoped ? (
        <div className={classes.detailSection}>
          <div className={classes.detailSectionLabel}>Strategy</div>
          <div className={classes.detailField}>
            <SydSelect
              options={ALLOCATION_STRATEGY_OPTIONS}
              value={column.options?.strategy || 'toParent'}
              onChange={(e) => onOptionChange(column.id, { strategy: e.target.value })}
              size='sm'
              minWidth='200px'
            />
          </div>
        </div>
      ) : null}
      {catalog.windowType === 'rank' || catalog.windowType === 'percentileRank' ? (
        <div className={classes.detailSection}>
          <div className={classes.detailSectionLabel}>Sort Direction</div>
          <div className={classes.detailField}>
            <SydSelect
              options={SORT_DIRECTION_OPTIONS}
              value={column.options?.sortDirection || 'desc'}
              onChange={(e) => onOptionChange(column.id, { sortDirection: e.target.value })}
              size='sm'
              minWidth='200px'
            />
          </div>
        </div>
      ) : null}
      <PersonalSpace size={60} />
    </div>
  )
}

WindowDetailPanel.propTypes = {
  column: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
  allColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
  classes: PropTypes.object.isRequired,
  onOptionChange: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired
}

function ComparisonDetailPanel ({ column, allColumns, classes, onOptionChange, onLabelChange }) {
  const catalog = COLUMN_CATALOG_MAP[column.columnType]
  const title = resolveColumnHeader(column, catalog, allColumns)

  const eligibleColumns = allColumns.filter(c => {
    const cat = COLUMN_CATALOG_MAP[c.columnType]
    return cat && cat.kind !== 'comparison' && cat.kind !== 'window'
  })

  const selectedAId = column.options?.columnAId
  const selectedA = selectedAId ? allColumns.find(c => c.id === selectedAId) : null
  const selectedAMeasureKind = selectedA ? COLUMN_CATALOG_MAP[selectedA.columnType]?.measureKind : null

  const columnAOptions = eligibleColumns.map(c => {
    const cat = COLUMN_CATALOG_MAP[c.columnType]
    return { value: c.id, label: c.label ?? cat?.label ?? c.columnType }
  })

  const columnBCandidates = selectedAMeasureKind
    ? eligibleColumns.filter(c => {
      const cat = COLUMN_CATALOG_MAP[c.columnType]
      return cat?.measureKind === selectedAMeasureKind
    })
    : eligibleColumns
  const columnBOptions = columnBCandidates.map(c => {
    const cat = COLUMN_CATALOG_MAP[c.columnType]
    return { value: c.id, label: c.label ?? cat?.label ?? c.columnType }
  })

  const handleColumnAChange = (e) => {
    const instanceId = e.target.value
    const instance = allColumns.find(c => c.id === instanceId)
    const newMeasureKind = instance ? COLUMN_CATALOG_MAP[instance.columnType]?.measureKind : null
    const currentB = column.options?.columnBId ? allColumns.find(c => c.id === column.options.columnBId) : null
    const currentBMeasureKind = currentB ? COLUMN_CATALOG_MAP[currentB.columnType]?.measureKind : null
    const clearB = newMeasureKind && currentBMeasureKind && newMeasureKind !== currentBMeasureKind
    onOptionChange(column.id, {
      columnAId: instanceId,
      columnAType: instance?.columnType || column.options?.columnAType,
      ...(clearB ? { columnBId: null, columnBType: null } : {})
    })
  }

  const handleColumnBChange = (e) => {
    const instanceId = e.target.value
    const instance = allColumns.find(c => c.id === instanceId)
    onOptionChange(column.id, {
      columnBId: instanceId,
      columnBType: instance?.columnType || column.options?.columnBType
    })
  }

  return (
    <div className={classes.detailPanel}>
      <DetailHeader title={title} catalog={catalog} classes={classes} />
      <ColumnNameInput column={column} catalog={catalog} allColumns={allColumns} classes={classes} onLabelChange={onLabelChange} />
      <div className={classes.detailSection}>
        <div className={classes.detailSectionLabel}>Column A</div>
        <div className={classes.detailField}>
          {columnAOptions.length > 0 ? (
            <SydSelect
              options={columnAOptions}
              value={selectedAId || ''}
              onChange={handleColumnAChange}
              size='sm'
              minWidth='200px'
            />
          ) : (
            <div className={classes.emptyHint} style={{ textAlign: 'left' }}>
              Add at least one data column first
            </div>
          )}
        </div>
      </div>
      <div className={classes.detailSection}>
        <div className={classes.detailSectionLabel}>Column B</div>
        <div className={classes.detailField}>
          {columnBOptions.length > 0 ? (
            <SydSelect
              options={columnBOptions}
              value={column.options?.columnBId || ''}
              onChange={handleColumnBChange}
              size='sm'
              minWidth='200px'
            />
          ) : (
            <div className={classes.emptyHint} style={{ textAlign: 'left' }}>
              {selectedAMeasureKind ? `Add another ${selectedAMeasureKind} column` : 'Select Column A first'}
            </div>
          )}
        </div>
      </div>
      <div className={classes.detailSection}>
        <div className={classes.detailSectionLabel}>Operation</div>
        <div className={classes.detailField}>
          <SydSelect
            options={COMPARISON_OPERATION_OPTIONS}
            value={column.options?.operation || 'difference'}
            onChange={(e) => onOptionChange(column.id, { operation: e.target.value })}
            size='sm'
            minWidth='200px'
          />
        </div>
      </div>
      <PersonalSpace size={60} />
    </div>
  )
}

ComparisonDetailPanel.propTypes = {
  column: PropTypes.object.isRequired,
  allColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
  classes: PropTypes.object.isRequired,
  onOptionChange: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired
}

function MaterialDetailPanel ({ column, catalog, allColumns, classes, onOptionChange, onLabelChange }) {
  const source = catalog ? CALC_SOURCES[catalog.calcSource] : null
  const supportsDate = source?.supportsDate
  const settingsSchema = source?.settingsSchema || []
  const isCustomRange = column.options?.dateMode === 'dateRange'
  const title = resolveColumnHeader(column, catalog, allColumns)

  const handleDateModeChange = (e) => {
    const { dateMode, quickDate } = parseDateModeValue(e.target.value)
    onOptionChange(column.id, {
      dateMode,
      quickDate,
      startDate: null,
      endDate: null
    })
  }

  return (
    <div className={classes.detailPanel}>
      <DetailHeader title={title} catalog={catalog} classes={classes} />
      <ColumnNameInput column={column} catalog={catalog} allColumns={allColumns} classes={classes} onLabelChange={onLabelChange} />

      {supportsDate ? (
        <div className={classes.detailSection}>
          <div className={classes.detailSectionLabel}>Date Range</div>
          <div className={classes.detailField}>
            <SydSelect
              options={DATE_MODE_OPTIONS}
              value={toDateModeSelectValue(column.options)}
              onChange={handleDateModeChange}
              size='sm'
              minWidth='200px'
            />
          </div>
          {isCustomRange ? (
            <div className={classes.dateRangeRow}>
              <input
                type='date'
                className={classes.dateInput}
                value={column.options?.startDate || ''}
                onChange={(e) => onOptionChange(column.id, { startDate: e.target.value || null })}
              />
              <input
                type='date'
                className={classes.dateInput}
                value={column.options?.endDate || ''}
                onChange={(e) => onOptionChange(column.id, { endDate: e.target.value || null })}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {settingsSchema.length > 0 ? (
        <div className={classes.detailSection}>
          <div className={classes.detailSectionLabel}>Options</div>
          {settingsSchema.map(setting => {
            if (setting.control === 'select') {
              return (
                <div key={setting.key} className={classes.detailField}>
                  <div className={classes.detailFieldLabel}>{setting.label}</div>
                  <SydSelect
                    options={setting.options}
                    value={column.options?.[setting.key] ?? setting.defaultValue}
                    onChange={(e) => onOptionChange(column.id, { [setting.key]: e.target.value })}
                    size='sm'
                    minWidth='200px'
                  />
                </div>
              )
            }
            if (setting.control === 'checkbox') {
              return (
                <label key={setting.key} className={classes.settingCheckLabel}>
                  <input
                    type='checkbox'
                    checked={column.options?.[setting.key] ?? setting.defaultValue}
                    onChange={(e) => onOptionChange(column.id, { [setting.key]: e.target.checked })}
                  />
                  {setting.label}
                </label>
              )
            }
            return null
          })}
        </div>
      ) : null}

      {!supportsDate && settingsSchema.length === 0 ? (
        <div className={classes.emptyHint}>No configurable options for this column</div>
      ) : null}
      <PersonalSpace size={60} />
    </div>
  )
}

MaterialDetailPanel.propTypes = {
  column: PropTypes.object.isRequired,
  catalog: PropTypes.object,
  allColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
  classes: PropTypes.object.isRequired,
  onOptionChange: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired
}

export default function ColumnDetailPanel ({ column, allColumns, onOptionChange, onLabelChange }) {
  const classes = useColumnBuilderStyles()

  if (!column) {
    return (
      <div className={classes.detailPanel}>
        <div className={classes.panelHeader}>Settings</div>
        <div className={classes.emptyHint}>Select a column to configure</div>
      </div>
    )
  }

  const catalog = COLUMN_CATALOG_MAP[column.columnType]

  if (catalog?.kind === 'derived') {
    return <DerivedDetailPanel column={column} catalog={catalog} allColumns={allColumns} classes={classes} onOptionChange={onOptionChange} onLabelChange={onLabelChange} />
  }
  if (catalog?.kind === 'window') {
    return <WindowDetailPanel column={column} catalog={catalog} allColumns={allColumns} classes={classes} onOptionChange={onOptionChange} onLabelChange={onLabelChange} />
  }
  if (catalog?.kind === 'comparison') {
    return <ComparisonDetailPanel column={column} allColumns={allColumns} classes={classes} onOptionChange={onOptionChange} onLabelChange={onLabelChange} />
  }
  return <MaterialDetailPanel column={column} catalog={catalog} allColumns={allColumns} classes={classes} onOptionChange={onOptionChange} onLabelChange={onLabelChange} />
}

ColumnDetailPanel.propTypes = {
  column: PropTypes.object,
  allColumns: PropTypes.arrayOf(PropTypes.object),
  onOptionChange: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired
}
